import { createSelector, createSlice } from '@reduxjs/toolkit';
import i18n from 'i18n';
import moment from 'moment';
import { setDefaultSettings } from './fuse/settingsSlice';

// import 'moment/locale/it';

interface CustomTransStructure {
	[key: string]: { [key: string]: string };
}

interface i18nState {
	language?: string;
	customTranslations: CustomTransStructure;
}

const initialState: i18nState = {
	language: i18n.options.lng,
	customTranslations: {}
};

export const changeLanguage = (languageId: string) => (dispatch, getState) => {
	const { direction } = getState().fuse.settings.defaults;
	const newLangDirection = i18n.dir(languageId);

	if (newLangDirection !== direction) {
		dispatch(setDefaultSettings({ direction: newLangDirection }));
	}

	moment.locale(languageId);

	return i18n.changeLanguage(languageId).then(() => {
		dispatch(i18nSlice.actions.languageChanged(languageId));
	});
};

export const loadCustomTranslations = (newTranslations?: CustomTransStructure) => dispatch => {
	if (!newTranslations) {
		const defaultCustomTranslations = Object.fromEntries(i18n.languages?.map(lng => [lng, {}]));
		dispatch(i18nSlice.actions.setCustomTranslations(defaultCustomTranslations));
	} else {
		const newTransArray: [string, { [key: string]: string }][] = Object.entries(newTranslations).map(
			([language, message]) => [language, message]
		);
		newTransArray.forEach(([language, message]) => i18n.addResources(language, 'custom', message));
		const newTrans = Object.fromEntries(newTransArray);
		dispatch(i18nSlice.actions.setCustomTranslations(newTrans));
	}
};

export const changeCustomTranslation = (language: string, key: string, message: string | null) => dispatch => {
	// TODO: add better null handler
	if (message !== null) i18n.addResource(language, 'custom', key, message);
	dispatch(i18nSlice.actions.changeCustomTranslation({ language, key, message }));
};

export const selectCurrentLanguageId = ({ i18n: _i18n }) => _i18n.language;
export const selectCurrentLanguageDirection = createSelector([selectCurrentLanguageId], id => i18n.dir(id));

export const selectCustomTranslations = ({ i18n: _i18n }) => _i18n.customTranslations;

const i18nSlice = createSlice({
	name: 'i18n',
	initialState,
	reducers: {
		languageChanged: (state, action) => {
			state.language = action.payload;
		},
		setCustomTranslations: (state, action) => {
			state.customTranslations = action.payload || {};
		},
		changeCustomTranslation: (state, action) => {
			const { language, key, message } = action.payload;
			if (!state.customTranslations[language]) state.customTranslations[language] = {};
			state.customTranslations[language][key] = message;
		}
	}
});

export default i18nSlice.reducer;
